import { combineReducers } from "redux";
import * as types from "../../src/features/partner/actionTypes";
import { reducer as oidcReducer } from "redux-oidc";
import { merge } from "lodash";
import initialState from "./initialState";
import ajaxCallsInProgress from "./ajaxStatusReducer";
import notificationReducer from "./notificationReducer";
import asyncCommandReducer from "./asyncCommandReducer";
import snackbarReducer from "./snackbarReducer";
import partners from "../features/partner/reducers/partnersReducer";
import partnerLogsReducer from "../features/partner/reducers/partnerLogsReducer";
import properties from "../features/partner/reducers/propertiesReducer";
import addresses from "../features/partner/reducers/addressesReducer";
import bankAccounts from "../features/partner/reducers/bankAccountsReducer";
import dictionaries from "../features/partner/reducers/dictionaryReducer";
import consents from "../features/partner/reducers/consentsReducer";
import contactInfos from "../features/partner/reducers/contactInfosReducer";
import summary from "../features/partner/reducers/summaryReducer";
import partnerContactModes from "../features/partner/reducers/contactModesReducer";
import shareholders from "../features/partner/reducers/shareholdersReducer";
import activityFields from "../features/partner/reducers/activityFieldsReducer";
import globalParamsReducer from "features/params/globalParams/reducer";
import scoringReducer from "features/partner/reducers/scoringReducer";
import dashboardReducer from "features/dashboard/dashboardReducer";
import userInfoReducer from "./userInfoReducer";
import userRights from "features/userRights/reducer";
import logsReducer from "features/logs/reducer";

import * as actionTypes from "../features/partner/actionTypes";
import schemas from "utils/schemas";
import { normalize } from "normalizr";

const entities = (state = initialState.entities, action) => {
  let result = state;

  if (action.type === actionTypes.UPDATE_PARTNER_SUCCEEDED) {
    const { key } = action;
    result = {
      ...state,
      partners: { ...state.partners, [key]: action.payload ? action.payload.partner : state.partners[key] }
    };
    return result;
  }

  if (action.type === actionTypes.UPDATE_LOGO_SUCCEEDED) {
    const { key } = action;
    result = {
      ...state,
      partners: {
        ...state.partners,
        [key]: action.payload
          ? {
              ...state.partners[key],
              logoUrl: action.payload.logoUrl
            }
          : state.partners[key]
      }
    };
    return result;
  }

  if (action.type === types.UPDATE_ADDRESS_SUCCEEDED) {
    const result = {
      ...state,
      addresses: { ...state.addresses }
    };
    const { partnerAddresses } = action;
    const filteredArray = Object.values(result.addresses).filter(
      x => !partnerAddresses.some(y => y.partnerAddressId === x.partnerAddressId)
    );

    filteredArray.forEach(x => {
      delete result.addresses[x.partnerAddressId];
    });

    partnerAddresses.forEach(x => {
      result.addresses[x.partnerAddressId] = { ...x };
    });

    return { ...state, addresses: { ...result.addresses } };
  }

  if (action.type === types.UPDATE_BANK_ACCOUNTS_SUCCEEDED) {
    const result = {
      ...state,
      bankAccounts: { ...state.bankAccounts }
    };
    const { bankAccounts } = action;
    const filteredArray = Object.values(result.bankAccounts).filter(
      x => !bankAccounts.some(y => y.bankAccountId === x.bankAccountId)
    );

    filteredArray.forEach(x => {
      delete result.bankAccounts[x.bankAccountId];
    });

    bankAccounts.forEach(x => {
      result.bankAccounts[x.bankAccountId] = { ...x };
    });

    return merge({}, state, normalize(bankAccounts, [schemas.bankAccount]).entities);
  }
  if (action.type === types.UPDATE_ACTIVITY_FIELDS_SUCCEEDED) {
    const result = {
      ...state,
      activityFields: { ...state.activityFields }
    };
    const { activityFields } = action;
    const filteredArray = Object.values(result.activityFields).filter(
      x => !activityFields.some(y => y.partnerActivityFieldId === x.partnerActivityFieldId)
    );

    filteredArray.forEach(x => {
      delete result.activityFields[x.partnerActivityFieldId];
    });

    activityFields.forEach(x => {
      result.activityFields[x.partnerActivityFieldId] = { ...x };
    });

    return { ...state, activityFields: { ...result.activityFields } };
  }

  if (action.type === types.UPDATE_CONTACT_MODES_SUCCEEDED) {
    const result = {
      ...state,
      partnerContactModes: { ...state.partnerContactModes }
    };
    const { contactInfoModes } = action;
    const filteredArray = Object.values(result.partnerContactModes).filter(
      x => !contactInfoModes.some(y => y.partnerContactModeId === x.partnerContactModeId)
    );

    filteredArray.forEach(x => {
      delete result.partnerContactModes[x.partnerContactModeId];
    });

    contactInfoModes.forEach(x => {
      result.partnerContactModes[x.partnerContactModeId] = { ...x };
    });

    return { ...state, partnerContactModes: { ...result.partnerContactModes } };
  }

  if (action.type === types.UPDATE_DEALER_CONTACT_INFOS_SUCCEEDED) {
    const result = {
      ...state,
      partnerDealerContactInfos: { ...state.partnerDealerContactInfos }
    };
    const { dealerContactInfos } = action;
    dealerContactInfos.forEach(x => {
      result.partnerDealerContactInfos[x.id] = { ...x };
    });

    return { ...state, partnerDealerContactInfos: { ...result.partnerDealerContactInfos } };
  }

  if (action.type === types.UPDATE_DEALER_CONTACT_MODES_SUCCEEDED) {
    const result = {
      ...state,
      partnerDealerContactModes: { ...state.partnerDealerContactModes }
    };
    const { dealerContactModes } = action;

    dealerContactModes.forEach(x => {
      result.partnerDealerContactModes[x.id] = { ...x };
    });

    return { ...state, partnerDealerContactModes: { ...result.partnerDealerContactModes } };
  }
  if (action.type === types.UPDATE_SHAREHOLDERS_SUCCEEDED) {
    const result = {
      ...state,
      shareholders: { ...state.shareholders }
    };
    const { shareholders, partnerId } = action;

    const filteredArray = Object.values(result.shareholders).filter(
      x => !shareholders.some(y => y.partnerShareHolderId === x.partnerShareHolderId)
    );

    filteredArray.forEach(x => {
      delete result.shareholders[x.partnerShareHolderId];
    });

    shareholders.forEach(x => {
      const shareholderDetail = Object.values(result.shareholderDetails).find(
        y => y.partnerId === x.shareHolderPartnerId
      );
      result.shareholders[x.partnerShareHolderId] = {
        ...x,
        uniqueID: shareholderDetail.uniqueID,
        shareHolderPartnerName: shareholderDetail.partnerName,
        shareHolderPhoneNumber: shareholderDetail.phoneNumber,
        shareholders: shareholderDetail.shareholders
      };
    });

    const filteredShareholdersSummary = Object.values(result.shareholders).filter(x => !x.isRealBeneficiary);

    const newShareholdersSummaryList = [...state.summary[partnerId].shareholders];

    filteredShareholdersSummary.forEach(x => {
      newShareholdersSummaryList.splice(newShareholdersSummaryList.indexOf(x.partnerShareHolderId), 1);
    });

    return {
      ...state,
      shareholders: { ...result.shareholders },
      summary: Object.assign(
        { ...state.summary },
        {
          [partnerId]: {
            ...state.summary[partnerId],
            shareholders: [...newShareholdersSummaryList]
          }
        }
      )
    };
  }
  if (action.type === types.UPDATE_CONSENTS_SUCCEEDED) {
    const result = {
      ...state,
      consents: { ...state.consents }
    };
    const { consents } = action;
    const filteredArray = Object.values(result.consents).filter(
      x => !consents.some(y => y.consentTypeId === x.consentTypeId)
    );

    filteredArray.forEach(x => {
      delete result.consents[x.consentTypeId];
    });

    consents.forEach(x => {
      result.consents[x.consentTypeId] = { ...x };
    });

    return { ...state, consents: { ...result.consents } };
  }

  if (action.type === types.LOAD_PARTNER_ACTIVITY_FIELDS_SUMMARY_SUCCEEDED) {
    const summary = { activityField: { ...action.payload }, id: action.id };
    return merge({}, state, normalize(summary, schemas.summary).entities);
  }

  if (action.type === types.LOAD_PARTNER_CONTACT_INFO_SUMMARY_SUCCEEDED) {
    const summary = { contactInfo: { ...action.payload }, id: action.id };
    return merge({}, state, normalize(summary, schemas.summary).entities);
  }

  if (action.type === types.LOAD_PARTNER_BANK_ACCOUNTS_SUMMARY_SUCCEEDED) {
    let summary = { bankAccounts: { ...action.payload }, id: action.id };

    if (!action.payload) summary = { bankAccounts: null, id: action.id };

    return merge({}, state, normalize(summary, schemas.summary).entities);
  }

  if (action.type === types.LOAD_PARTNER_ADDRESSES_SUMMARY_SUCCEEDED) {
    let summary = { partnerAddresses: { ...action.payload }, id: action.id };

    if (!action.payload) summary = { partnerAddresses: null, id: action.id };

    return merge({}, state, normalize(summary, schemas.summary).entities);
  }

  if (action.type === types.LOAD_PARTNER_SHAREHOLDERS_SUMMARY_SUCCEEDED) {
    let summary = { shareholders: [...action.payload], id: action.id };

    if (!action.payload) summary = { shareholders: null, id: action.id };

    return merge({}, state, normalize(summary, schemas.summary).entities);
  }

  if (action.entities) {
    result = merge({}, state, action.entities);
  }

  return result;
};

const reducers = combineReducers({
  oidc: oidcReducer,
  ajaxCallsInProgress,
  notifications: notificationReducer,
  pendingAsyncCommands: asyncCommandReducer,
  snackbar: snackbarReducer,
  entities,
  partners,
  partnerLogs: partnerLogsReducer,
  properties,
  addresses,
  bankAccounts,
  consents,
  contactInfos,
  summary,
  partnerContactModes,
  shareholders,
  dictionaries,
  activityFields,
  params: globalParamsReducer,
  scoringGrid: scoringReducer,
  dashboard: dashboardReducer,
  userInfo: userInfoReducer,
  userRights,
  logsData: logsReducer
});

export default reducers;
